import { Icon } from "../components/Common.jsx";
import { ChildContextBar } from "../components/ChildContextBar.jsx";
import { KidChips } from "../components/KidChips.jsx";
import { fmtPence, spendingCapPence, tokensToPence } from "../lib/utils.js";
import {
  alignLinkedTask,
  applySuggestedValues,
  flagForSource,
  linkedDrift,
  projectBusyWeekTokens,
  repriceToCap,
  suggestedValues,
} from "../lib/spendingDefaults.js";
import { CURRENCY_SYMBOL, DEFAULT_SPENDING_CAP_GBP, MIN_REWARD_TOKENS } from "../lib/constants.js";
import { t as ct } from "../lib/content.js";
const { useState } = React;

// Spending review — the "is my setup still sane?" page.
//
// WHY IT IS NOT IN THE WIZARD. At setup time there is nothing to review: the numbers are
// the ones this engine just derived from the parent's own cap. This page earns its keep
// LATER, once a parent has added tasks, nudged rewards, or changed a cap — which is exactly
// when values drift past the cap without anyone noticing until a child gets blocked.
//
// EVERY ACTION HERE IS A TAP. Nothing on this page rewrites a value on its own: the flags
// are advisory, and Apply / Align / Reprice each do one thing the parent asked for. That is
// the whole governing rule of this feature — a parent must never find a number changed and
// not know why — and it is why there is no toast on the per-row actions either: the row
// itself visibly changes, so announcing it twice would just be noise.

function Bar({ projection }) {
  const { tokens, capTokens, phantom } = projection;
  if (!capTokens) return null;
  const over = tokens > capTokens;
  const configuredPct = Math.min(100, Math.round(((tokens - phantom) / capTokens) * 100));
  const phantomPct = Math.min(100 - configuredPct, Math.round((phantom / capTokens) * 100));
  return (
    <div className="h-3 rounded-full bg-slate-100 overflow-hidden relative flex" data-testid="review-budget-bar" data-over={over ? "1" : "0"}>
      <div className={`h-full ${over ? "bg-rose-500" : "bg-amber-400"}`} style={{ width: `${configuredPct}%` }} />
      {/* The reserved task slot, drawn hollow: it is room we've left, not money committed. */}
      {phantomPct > 0 && <div className="h-full border-y border-r border-dashed border-amber-400 bg-amber-100" style={{ width: `${phantomPct}%` }} />}
    </div>
  );
}

export function SpendingReview({ child, state, update, showToast, profiles, onSwitchChild, hiddenIds }) {
  // Every hook before the early return — this component is mounted from a modal whose child
  // can change under it, and a conditional hook order is the classic way that breaks.
  const [confirmReset, setConfirmReset] = useState(false);
  const [capDraft, setCapDraft] = useState(null);
  // Rows the parent has typed into but which have not been accepted — an amount below the
  // floor, or not a number at all.
  //
  // WHY THIS EXISTS (Alan, 30 Aug 2026). The row inputs are uncontrolled and keyed on their
  // committed value, so a bulk action that rewrote every amount also silently remounted
  // every input — throwing away whatever the parent had typed, including the value they
  // were in the middle of correcting. They watched their own number vanish with no
  // explanation, which is the exact failure this whole feature exists to prevent, committed
  // by the button meant to fix it. Bulk actions are now blocked while anything is
  // outstanding, and the reason is on screen.
  const [invalidRows, setInvalidRows] = useState({});
  const hasInvalid = Object.keys(invalidRows).length > 0;
  if (!child) return null;
  const projection = projectBusyWeekTokens(child, state);
  const capPence = spendingCapPence(child);
  const suggestion = suggestedValues(child, state);

  function saveCap(raw) {
    const n = parseFloat(raw);
    setCapDraft(null);
    if (!n || n <= 0) return;
    update((s) => {
      const c = s.profiles.find((p) => p.id === child.id);
      if (!c) return null;
      c.spendingCap = { ...(c.spendingCap || {}), enabled: true, weeklyGBP: Math.round(n * 100) / 100 };
      return s;
    });
  }

  /* Line-level editing (Alan, 30 Aug 2026). Reading a list of amounts and then having to
   * go and find each one somewhere else to change it is the wrong shape for a review: the
   * page has already worked out what is wrong, so the fix belongs on the row that says so.
   * Writes straight through to whatever actually stores that number — the subject's full
   * rewards, the child's habit reward, or the task row. */
  function setSourceTokens(source, tokens) {
    const raw = String(tokens).trim();
    const n = parseInt(raw, 10);
    // Coercing a bad value into a good one silently is its own kind of overwrite: the
    // parent typed something, and replacing it with a number they did not choose is worse
    // than telling them it will not do.
    if (raw === "" || Number.isNaN(n) || n < MIN_REWARD_TOKENS) {
      setInvalidRows((m) => ({ ...m, [source.id]: raw }));
      return;
    }
    setInvalidRows((m) => { const next = { ...m }; delete next[source.id]; return next; });
    const v = n;
    update((s) => {
      const lc = (s.learningContent || {})[child.id];
      const c = s.profiles.find((p) => p.id === child.id);
      if (source.kind === "english" && lc) {
        // LB-355: every pass reward follows its full reward, spelling included (it was the one left behind).
        if (lc.spellingEnabled !== false) { lc.spellingReward = v; lc.spellingPassReward = Math.max(MIN_REWARD_TOKENS, Math.round(v * 0.6)); }
        if (lc.vocabEnabled !== false) { lc.vocabFullReward = v; lc.vocabPassReward = Math.max(MIN_REWARD_TOKENS, Math.round(v * 0.6)); }
      } else if (source.kind === "maths" && lc) {
        if (lc.ttstreaksEnabled !== false) { lc.ttStreakFullReward = v; lc.ttStreakPassReward = Math.max(MIN_REWARD_TOKENS, Math.round(v * 0.6)); }
        if (lc.mathsworkoutEnabled !== false) { lc.mathsFullReward = v; lc.mathsPassReward = Math.max(MIN_REWARD_TOKENS, Math.round(v * 0.6)); }
      } else if (source.kind === "habit" && c) {
        c.habitReward = v;
      } else if (source.kind === "task") {
        const row = (s.activities || []).find((a) => a.id === source.id);
        if (row) row.tokens = v;
      } else {
        return null;
      }
      return s;
    });
  }

  function applySuggestionTo(source) {
    if (!suggestion) return;
    update((s) => {
      const lc = (s.learningContent || {})[child.id];
      const c = s.profiles.find((p) => p.id === child.id);
      if (source.kind === "english" && lc) {
        // LB-355: the suggestion sets each game's pass reward with its full one, as every other
        // resize does. This one set full rewards only, for all four games.
        const pass = (n) => Math.max(MIN_REWARD_TOKENS, Math.round(n * 0.6));
        if (lc.spellingEnabled !== false) { lc.spellingReward = suggestion.englishPerPayout; lc.spellingPassReward = pass(suggestion.englishPerPayout); }
        if (lc.vocabEnabled !== false) { lc.vocabFullReward = suggestion.englishPerPayout; lc.vocabPassReward = pass(suggestion.englishPerPayout); }
      } else if (source.kind === "maths" && lc) {
        const pass = (n) => Math.max(MIN_REWARD_TOKENS, Math.round(n * 0.6));
        if (lc.ttstreaksEnabled !== false) { lc.ttStreakFullReward = suggestion.mathsPerPayout; lc.ttStreakPassReward = pass(suggestion.mathsPerPayout); }
        if (lc.mathsworkoutEnabled !== false) { lc.mathsFullReward = suggestion.mathsPerPayout; lc.mathsPassReward = pass(suggestion.mathsPerPayout); }
      } else if (source.kind === "habit" && c) {
        c.habitReward = suggestion.habit;
      } else if (source.kind === "task") {
        const tasks = (s.activities || []).filter((a) => a.childId === child.id);
        const each = Math.max(MIN_REWARD_TOKENS, Math.round(suggestion.taskSlot / Math.max(1, tasks.length)));
        const row = (s.activities || []).find((a) => a.id === source.id);
        if (row) row.tokens = each;
      }
      return s;
    });
  }

  function doReprice() {
    let result = null;
    update((s) => { result = repriceToCap(s, child.id); return result && result.changed ? s : null; });
    if (!showToast || !result) return;
    if (!result.changed) {
      showToast(ct("toast.reprice_nothing", "Nothing to rebalance — everything is already as low as it goes.", { name: child.name }), "info");
    } else if (!result.fits) {
      showToast((result.dropped && result.dropped.length ? ct("toast.reprice_trimmed_over", "Rebalanced and removed {list} — still {over} tokens over the cap. Lower a reward or remove a task to fit.", { list: result.dropped.join(", "), over: result.over }) : ct("toast.reprice_over", "Still {over} tokens over the cap — lower a reward or remove a task to fit.", { over: result.over, name: child.name })), "info");
    } else {
      showToast((result.dropped && result.dropped.length ? ct("toast.reprice_trimmed", "Rebalanced to fit {name}'s cap. Removed {list}", { name: child.name, list: result.dropped.join(", ") }) : ct("toast.repriced", "Rewards rebalanced to fit {name}'s cap", { name: child.name })), "good");
    }
  }

  function doResetToSuggested() {
    // Same honesty rule as Reprice above: say what actually happened. Reset now runs the
    // same fit pass, so "over the cap straight after a reset" is gone — but a child with
    // more earning sources than the cap can pay for at the 2-token floor still can't be
    // made to fit by any amount of arithmetic, and that case gets told rather than claimed.
    let result = null;
    update((s) => { result = applySuggestedValues(s, child.id); return result && result.applied ? s : null; });
    setConfirmReset(false);
    if (!showToast) return;
    if (result && result.applied && !result.fits) {
      showToast(ct("toast.reset_partial", "Reset as far as it goes — {name} has more rewards than the cap can cover.", { name: child.name }), "info");
      return;
    }
    showToast(ct("toast.reset_suggested", "Reset to suggested amounts"), "good");
  }

  function doAlign(taskId, siblingTaskId) {
    update((s) => { alignLinkedTask(s, taskId, siblingTaskId); return s; });
  }

  const capTokens = projection.capTokens;
  const overBy = capTokens ? projection.tokens - capTokens : 0;
  const overPence = tokensToPence(Math.abs(overBy), projection.rate);

  return (
    <div className="flex flex-col gap-2" data-testid="spending-review">
      {/* Which child (Alan, 3 Oct): the Kids tab's own name pills, no "+", above the bar.
          One child, no pills. The bar itself only says who; it has no Switch. */}
      {onSwitchChild && profiles && profiles.length > 1 && (
        <KidChips profiles={profiles} activeId={child.id} onSelect={onSwitchChild} hiddenIds={hiddenIds} testId="review-kid-chips" />
      )}
      <ChildContextBar
        child={child}
        contextLine={ct("review.context", "Reviewing {name}'s earning settings", { name: child.name })}
        sticky={false}
      />

      {capTokens === null ? (
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 text-sm text-slate-500">
          {ct("review.cap_off", "{name}'s weekly cap is switched off, so there's nothing to measure their rewards against. Turn it on to see how a busy week adds up.", { name: child.name })}
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100" data-testid="review-budget-card">
          <div className="flex items-center justify-between text-xs font-medium text-slate-500 mb-1.5">
            <span data-testid="review-projection">{ct("review.busy_week", "Busy week: {tok} tokens ({gbp})", { tok: projection.tokens, gbp: fmtPence(projection.pence) })}</span>
            <span>{ct("review.cap_label", "Cap: {cap}", { cap: fmtPence(capPence) })}</span>
          </div>
          <Bar projection={projection} />
          <p className={`text-xs font-bold leading-snug mt-2 ${projection.state === "over" ? "text-rose-700" : projection.state === "warn" ? "text-amber-800" : "text-emerald-700"}`} data-testid="review-verdict">
            {projection.state === "over"
              ? ct("review.verdict_over", "{name} can earn {over} more than the cap allows. The cap will block the extra — but being blocked is what makes kids give up.", { name: child.name, over: fmtPence(overPence) })
              : projection.state === "warn"
                ? ct("review.verdict_warn", "A busy week only reaches {pct}% of the cap. There's room to make rewards feel worth the effort.", { pct: Math.round((projection.tokens / capTokens) * 100) })
                : ct("review.verdict_ok", "Comfortably inside the cap, with {left} of headroom.", { left: fmtPence(overBy < 0 ? overPence : 0) })}
          </p>
          {projection.state !== "ok" && (
            <>
              <button type="button" onClick={doReprice} data-testid="review-reprice" disabled={hasInvalid}
                className={`mt-2.5 w-full text-xs font-bold rounded-xl py-2.5 ${hasInvalid ? "bg-slate-200 text-slate-400" : "bg-emerald-600 text-white"}`}>
                {ct("review.reprice_all", "Rebalance everything to fit")}
              </button>
              {hasInvalid && (
                <p className="text-xs font-bold text-rose-700 mt-1.5" data-testid="review-reprice-blocked">
                  {ct("review.reprice_blocked", "Fix the highlighted amount first — rebalancing would replace what you typed.")}
                </p>
              )}
            </>
          )}
        </div>
      )}

      <div className="text-sm font-semibold text-slate-900 px-1 pt-1">
        {ct("review.sources_group", "Where {name}'s tokens come from", { name: child.name })}
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 divide-y divide-slate-100">
        {projection.sources.length === 0 && (
          <p className="text-xs text-slate-400 p-4">{ct("review.no_sources", "Nothing set up yet — add a task or switch on a Learn & Earn game.")}</p>
        )}
        {projection.sources.map((source) => {
          const flag = flagForSource(source.weekly, capTokens);
          const share = capTokens ? Math.round((source.weekly / capTokens) * 100) : 0;
          const task = source.kind === "task" ? (state.activities || []).find((a) => a.id === source.id) : null;
          const drift = task ? linkedDrift(state, task) : null;
          return (
            <div key={source.id} className="p-3" data-testid="review-source-row">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
                  <Icon id={source.kind === "english" ? "ic-spelling" : source.kind === "maths" ? "ic-maths" : source.kind === "habit" ? "ic-flame" : "ic-medal"} size={16} hue={source.kind === "habit" ? "teal" : "gold"} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-slate-900 truncate">{source.label}</div>
                  <div className="text-xs text-slate-400 font-medium truncate">
                    {source.payouts > 1 ? ct("review.source_payouts_short", "{n} payouts a week", { n: source.payouts }) : ct("review.source_weekly_short", "once a week")}
                    {drift ? ` · ${ct("review.linked_with", "linked with {name}'s", { name: drift.siblingChild ? drift.siblingChild.name : "a sibling" })}` : ""}
                  </div>
                </div>
                {/* The token amount IS the subject of this page, so it gets the weight — a
                    big editable number, not a muted sub-label. */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <input
                    className={`w-14 text-right text-base font-extrabold rounded-lg px-2 py-1 border ${invalidRows[source.id] !== undefined ? "border-rose-400 text-rose-700 bg-rose-50" : "border-slate-200 text-slate-900"}`}
                    inputMode="numeric"
                    data-testid="review-source-tokens"
                    aria-label={`${source.label} reward in tokens`}
                    aria-invalid={invalidRows[source.id] !== undefined || undefined}
                    defaultValue={source.each}
                    key={`${source.id}-${source.each}`}
                    onBlur={(e) => setSourceTokens(source, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                  />
                  <span className="text-xs font-medium text-slate-400">{ct("review.tokens_unit", "tok")}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1.5 ml-10">
                {drift ? (
                  <span className="text-xs font-bold px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 flex-shrink-0" data-testid="review-badge-drifted">{ct("review.badge_drifted", "Drifted")}</span>
                ) : flag === "high" || flag === "low" ? (
                  /* LB-285 (Alan, 14 Sep): "can we only show the % pill label if the percentage
                     is a problem? These numbers are meaningless to a human reviewing them."
                     He is right about the OK case specifically: "OK · 21%" gives a parent a
                     figure with nothing to compare it against, on a row that is fine. The
                     percentage survives on HIGH, where the number IS the argument for doing
                     something about it. */
                  <span className={`text-xs font-bold px-2 py-0.5 rounded flex-shrink-0 ${flag === "high" ? "bg-amber-50 text-amber-800 border border-amber-200" : flag === "low" ? "bg-slate-100 text-slate-400" : "bg-emerald-50 text-emerald-700"}`} data-testid={`review-badge-${flag}`}>
                    {flag === "high" ? ct("review.badge_high", "High · {pct}%", { pct: share }) : ct("review.badge_low", "Low")}
                  </span>
                ) : null}
              </div>

              {flag === "high" && !drift && suggestion && (
                <div className="mt-2 ml-10 bg-amber-50 border border-amber-200 rounded-xl px-2.5 py-2 flex items-center gap-2">
                  <p className="text-xs font-semibold text-amber-800 leading-snug flex-1 min-w-0">
                    {ct("review.fix_high", "This alone uses {pct}% of the weekly cap. Suggested: {suggested} tokens.", {
                      pct: share,
                      suggested: source.kind === "english" ? suggestion.englishPerPayout : source.kind === "maths" ? suggestion.mathsPerPayout : source.kind === "habit" ? suggestion.habit : suggestion.taskSlot,
                    })}
                  </p>
                  <button type="button" data-testid="review-apply" className="bg-amber-700 text-white text-xs font-bold rounded-lg px-2.5 py-1.5 flex-shrink-0" onClick={() => applySuggestionTo(source)}>
                    {ct("review.apply", "Apply")}
                  </button>
                </div>
              )}
              {invalidRows[source.id] !== undefined && (
                <p className="mt-2 ml-10 text-xs font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-2.5 py-2" data-testid="review-row-error">
                  {ct("review.row_invalid", "Enter a whole number of {min} or more. Your amount hasn't been saved.", { min: MIN_REWARD_TOKENS })}
                </p>
              )}
              {flag === "low" && !drift && (
                <p className="mt-2 ml-10 text-xs font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-2.5 py-2">
                  {ct("review.fix_low", "Too small to be worth the effort — most children ignore rewards under {min} tokens.", { min: MIN_REWARD_TOKENS })}
                </p>
              )}
              {drift && (
                <div className="mt-2 ml-10 bg-amber-50 border border-amber-200 rounded-xl px-2.5 py-2 flex items-center gap-2">
                  <p className="text-xs font-semibold text-amber-800 leading-snug flex-1 min-w-0">
                    {ct("review.fix_drift", "{name} gets {apct}% of their cap for this; {sib} gets {bpct}% of theirs.", {
                      name: child.name,
                      apct: Math.round(drift.mineShare * 100),
                      sib: drift.siblingChild ? drift.siblingChild.name : "their sibling",
                      bpct: Math.round(drift.theirShare * 100),
                    })}
                  </p>
                  <button type="button" data-testid="review-align" className="bg-amber-700 text-white text-xs font-bold rounded-lg px-2.5 py-1.5 flex-shrink-0" onClick={() => doAlign(task.id, drift.sibling.id)}>
                    {ct("review.align", "Align")}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-sm font-semibold text-slate-900 px-1 pt-1">
        {ct("review.settings_group", "{name}'s settings", { name: child.name })}
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 divide-y divide-slate-100">
        <div className="flex items-center gap-2.5 p-3">
          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0"><Icon id="ic-cap" size={16} hue="gold" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-slate-900">{ct("review.cap_row", "Weekly cap")}</div>
            <div className="text-xs text-slate-400 font-medium">{ct("review.cap_row_hint", "The most {name} can earn in a week", { name: child.name })}</div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-xs font-medium text-slate-400">{CURRENCY_SYMBOL}</span>
            <input className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs w-20 text-right font-bold" type="number" min="0.01" step="1"
              data-testid="review-cap-input"
              value={capDraft ?? (child.spendingCap?.weeklyGBP ?? DEFAULT_SPENDING_CAP_GBP)}
              onChange={(e) => setCapDraft(e.target.value)}
              onBlur={(e) => saveCap(e.target.value)} />
          </div>
        </div>
        <div className="flex items-center gap-2.5 p-3">
          <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0"><Icon id="ic-exchange" size={16} /></div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-slate-900">{ct("review.rate_row", "Exchange rate")}</div>
            <div className="text-xs text-slate-400 font-medium">
              {ct("review.rate_row_hint", "{rate} tokens = {symbol}1, the same for everyone in the household", { rate: projection.rate, symbol: CURRENCY_SYMBOL })}
            </div>
          </div>
        </div>
      </div>

      {/* Reset is the blunt instrument, so it sits at the bottom behind a confirm rather
          than beside Reprice: Reprice keeps the parent's own balance between rewards, this
          throws it away. Most people want the first one. */}
      <div className="pt-1">
        {confirmReset ? (
          <div className="bg-white rounded-2xl p-3 shadow-sm border border-slate-200">
            <p className="text-xs font-medium text-slate-600 mb-2">{ct("review.reset_confirm", "This replaces {name}'s reward amounts with our suggestions, sized to their cap. Your own amounts will be lost.", { name: child.name })}</p>
            <div className="flex gap-2">
              <button type="button" className="flex-1 bg-slate-900 text-white text-xs font-bold rounded-xl py-2" data-testid="review-reset-confirm" onClick={doResetToSuggested}>{ct("review.reset_confirm_yes", "Reset amounts")}</button>
              <button type="button" className="flex-1 bg-white border border-slate-200 text-slate-500 text-xs font-bold rounded-xl py-2" onClick={() => setConfirmReset(false)}>{ct("wizard.common.cancel", "Cancel")}</button>
            </div>
          </div>
        ) : (
          <button type="button" disabled={hasInvalid} className={`w-full text-xs font-bold underline underline-offset-2 py-1 ${hasInvalid ? "text-slate-300" : "text-slate-400"}`} data-testid="review-reset" onClick={() => setConfirmReset(true)}>
            {ct("review.reset_to_suggested", "Reset to suggested amounts")}
          </button>
        )}
      </div>
    </div>
  );
}
